import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';

import type { Form, FormField } from '../../interfaces/form.interface';
import type {
  DropdownData,
  FilteredValue,
  RepeatableComputeContext,
  RepeatableGroupConfiguration,
  RepeatableSubField,
} from '../../interfaces/input.interface';
import { InputType } from '../../interfaces/input.interface';

type RowValue = Record<string, unknown>;

@Component({
  selector: 'app-repeatable-group',
  standalone: false,
  templateUrl: './repeatable-group.component.html',
  styleUrls: ['./repeatable-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepeatableGroupComponent<T> implements OnInit, OnDestroy {
  @Input({ required: true }) field!: FormField<T>;
  @Input({ required: true }) model!: FormGroup;
  @Input({ required: true }) form!: Form<T>;
  @Input() isEditable = true;

  public readonly INPUT = InputType;

  public rowsForm!: FormGroup;
  public rowsArray!: FormArray<FormGroup>;

  private readonly destroyed$ = new Subject<void>();

  /** Toggle debug logs */
  private readonly DEBUG = false;

  /** ids we couldn’t resolve because dropdown data was not ready yet */
  private pendingDropdown: Array<{
    rowIndex: number;
    logicalKey: string;
    rawId: string;
    sf: RepeatableSubField;
  }> = [];

  private pendingResolveTimer: number | null = null;
  private pendingResolveAttempt = 0;

  private get cfg(): RepeatableGroupConfiguration {
    const cfg = this.field?.input?.repeatableGroupConfiguration;
    if (!cfg) throw new Error('Repeatable group configuration is missing.');
    return cfg;
  }

  public ngOnInit(): void {
    this.debugLog('ngOnInit()', {
      isEditable: this.isEditable,
      bindings: this.cfg.bindings,
      serialization: this.cfg.serialization,
      subFields: (this.cfg.subFields ?? []).map((s) => s.key),
    });

    this.bootstrapForm();
    this.hydrateFromModel();
    this.bindRecomputePipeline();
  }

  public ngOnDestroy(): void {
    this.debugLog('ngOnDestroy()');

    if (this.pendingResolveTimer != null) {
      clearTimeout(this.pendingResolveTimer);
      this.pendingResolveTimer = null;
    }

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // -----------------------------
  // UI helpers
  // -----------------------------

  public visibleSubFields(): RepeatableSubField[] {
    const subFields = this.cfg.subFields ?? [];

    return subFields.filter((sf) => (typeof sf.isVisible === 'function' ? sf.isVisible() : true));
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public get currentCount(): number {
    return this.rowsArray?.length ?? 0;
  }

  public get maxRows(): number {
    const max = Number(this.cfg.rows ?? 0);
    return Number.isFinite(max) && max > 0 ? max : 1;
  }

  public canAddRow(): boolean {
    return this.isEditable && this.currentCount < this.maxRows;
  }

  public canDeleteRow(): boolean {
    return this.isEditable && this.currentCount > 1;
  }

  public addRow(): void {
    if (!this.canAddRow()) return;

    this.debugLog('addRow() before', { len: this.rowsArray.length });

    this.rowsArray.push(this.createRowGroup());
    this.recomputeAllAndSync();

    this.debugLog('addRow() after', { len: this.rowsArray.length });
  }

  public deleteRow(rowIndex: number): void {
    if (!this.isEditable) return;
    if (rowIndex < 0 || rowIndex >= this.currentCount) return;

    this.debugLog('deleteRow()', { rowIndex, beforeLen: this.rowsArray.length });

    this.rowsArray.removeAt(rowIndex);

    if (this.rowsArray.length === 0) {
      this.rowsArray.push(this.createRowGroup());
    }

    this.recomputeAllAndSync();

    this.debugLog('deleteRow() done', { afterLen: this.rowsArray.length });
  }

  public readOnlyCellText(sf: RepeatableSubField, rowIndex: number): string {
    const ctrl = this.rowsArray.at(rowIndex)?.get(sf.key);
    const v = ctrl?.value;

    if (this.DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[RepeatableGroup][readOnlyCellText]', { rowIndex, key: sf.key, type: sf.input?.type, value: v });
    }

    if (v == null || String(v).trim() === '') {
      return sf.input?.type === InputType.ONLY_TEXT ? '0,00' : '-';
    }

    // IMPORTANT: In info mode, values may already be labels (names), not IDs.
    // If we detect "already label", return it directly.
    if (this.isInfoLabelValue(sf, v)) {
      const direct = this.normalizeLabelText(v);

      if (this.DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[RepeatableGroup][readOnlyCellText] treated as LABEL', { key: sf.key, direct });
      }

      return direct || '-';
    }

    switch (sf.input?.type) {
      case InputType.DROPDOWN:
      case InputType.DROPDOWN_SEARCH:
        return this.resolveDropdownLabel(sf, v) ?? '-';

      case InputType.MULTISELECT:
      case InputType.MULTISELECT_SEARCH:
        return this.resolveMultiLabels(sf, v) ?? '-';

      default:
        if (typeof v === 'object') {
          const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
          const id = (v as any)?.[pk] ?? (v as any)?.id;
          return id != null ? String(id) : '-';
        }
        return String(v);
    }
  }

  // -----------------------------
  // Mode-aware: label vs id
  // -----------------------------

  /**
   * Info view can provide "names" directly.
   * Treat as label if:
   * - not editable AND
   * - subfield is dropdown/multiselect AND
   * - value is string and NOT a pure numeric id AND
   * - dropdown options are not ready or don't contain that id.
   */
  private isInfoLabelValue(sf: RepeatableSubField, raw: unknown): boolean {
    if (this.isEditable) return false;

    const t = sf.input?.type;
    const isSelect =
      t === InputType.DROPDOWN ||
      t === InputType.DROPDOWN_SEARCH ||
      t === InputType.MULTISELECT ||
      t === InputType.MULTISELECT_SEARCH;

    if (!isSelect) return false;

    if (raw == null) return false;

    // if it is object, it's not a label string (we'll handle normally)
    if (typeof raw === 'object') return false;

    const s = String(raw).trim();
    if (!s) return false;

    // Support "label;label;label" too in info view.
    const parts = s.split(';').map((x) => x.trim()).filter((x) => x !== '');
    if (parts.length === 0) return false;

    // If all parts are "numeric-like", it's probably ids, not labels.
    const allNumericLike = parts.every((p) => this.isNumericLike(p));
    if (allNumericLike) return false;

    // If options are loaded and contain any id match, prefer id mapping.
    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const opts = this.dropdownOptions(sf) ?? [];

    if (opts.length > 0) {
      const anyPartIsKnownId = parts.some((p) => opts.some((o) => String(o?.value?.[pk]) === p));
      if (anyPartIsKnownId) return false;
    }

    return true;
  }

  private normalizeLabelText(raw: unknown): string {
    // Info view may provide "A;B;C" -> display "A, B, C"
    const s = String(raw ?? '').trim();
    if (!s) return '';

    const parts = s.split(';').map((x) => x.trim()).filter((x) => x !== '');

    return parts.join(', ');
  }

  private isNumericLike(value: string): boolean {
    // numeric-like: "123", "001", "12.3" etc.
    // If your IDs are UUID/strings, they will not be numeric-like.
    const s = value.trim();
    if (!s) return false;

    // eslint-disable-next-line no-restricted-globals
    return !Number.isNaN(Number(s));
  }

  // -----------------------------
  // Dropdown / labels (ID mapping)
  // -----------------------------

  private parseSerializedIds(raw: unknown, pk: string): string[] {
    if (raw == null) return [];

    if (Array.isArray(raw)) {
      return raw
        .map((x) => this.normalizeToId(x, pk))
        .filter((x): x is string => !!x);
    }

    if (typeof raw === 'string' || typeof raw === 'number') {
      return String(raw)
        .split(';')
        .map((x) => x.trim())
        .filter((x) => x !== '');
    }

    const one = this.normalizeToId(raw, pk);

    return one ? [one] : [];
  }

  private idsToLabels(sf: RepeatableSubField, ids: string[]): string[] {
    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const opts = this.dropdownOptions(sf) ?? [];

    const labels = ids
      .map((id) => opts.find((o) => String(o?.value?.[pk]) === id)?.label ?? '')
      .filter((l) => l.trim() !== '');

    return labels;
  }

  private resolveDropdownLabel(sf: RepeatableSubField, raw: unknown): string | null {
    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';

    const ids = this.parseSerializedIds(raw, pk);
    if (ids.length === 0) return null;

    const labels = this.idsToLabels(sf, ids);

    if (this.DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[RepeatableGroup][resolveDropdownLabel]', { key: sf.key, raw, ids, labels, optionsLen: this.dropdownOptions(sf).length });
    }

    return labels.length ? labels.join(', ') : null;
  }

  private resolveMultiLabels(sf: RepeatableSubField, raw: unknown): string | null {
    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';

    const ids = this.parseSerializedIds(raw, pk);
    if (ids.length === 0) return null;

    const labels = this.idsToLabels(sf, ids);

    if (this.DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[RepeatableGroup][resolveMultiLabels]', { key: sf.key, raw, ids, labels, optionsLen: this.dropdownOptions(sf).length });
    }

    return labels.length ? labels.join(', ') : null;
  }

  private normalizeToId(raw: unknown, pk: string): string | null {
    if (raw == null) return null;

    if (typeof raw === 'string' || typeof raw === 'number') {
      const s = String(raw).trim();
      return s ? s : null;
    }

    if (typeof raw === 'object') {
      const obj = raw as any;

      if (obj.value && typeof obj.value === 'object') {
        const id = obj.value?.[pk] ?? obj.value?.id;
        return id != null ? String(id).trim() : null;
      }

      const id = obj?.[pk] ?? obj?.id;
      return id != null ? String(id).trim() : null;
    }

    return null;
  }

  public dropdownOptions(sf: RepeatableSubField): Array<FilteredValue<any>> {
    const dd = sf.input?.data as DropdownData<any> | undefined;
    const cfg = sf.input?.dropdownConfiguration;

    const filtered = (dd?.filtered ?? []) as Array<FilteredValue<any>>;
    const values = (dd?.values ?? []) as Array<FilteredValue<any>>;

    const filteredHasRealValues = filtered.some((it) => it?.value != null);
    const valuesHasRealValues = values.some((it) => it?.value != null);

    let base: Array<FilteredValue<any>> = [];

    if (cfg?.filteredValues?.length) {
      base = cfg.filteredValues as any;
    } else if (valuesHasRealValues) {
      base = values;
    } else if (filteredHasRealValues) {
      base = filtered;
    } else {
      base = [];
    }

    const filterFn = cfg?.filter as ((it: any) => boolean) | undefined;
    const afterFilter = filterFn ? base.filter((it) => filterFn(it) !== false) : base;

    return afterFilter as Array<FilteredValue<any>>;
  }

  // -----------------------------
  // Form / serialization
  // -----------------------------

  private bootstrapForm(): void {
    this.rowsArray = new FormArray<FormGroup>([]);
    this.rowsArray.push(this.createRowGroup());

    this.rowsForm = new FormGroup({
      rows: this.rowsArray,
    });
  }

  private createRowGroup(): FormGroup {
    const group: Record<string, AbstractControl> = {};

    for (const sf of this.cfg.subFields) {
      group[sf.key] = new FormControl<string | number | null>(null, { nonNullable: false });
    }

    return new FormGroup(group);
  }

  private bindRecomputePipeline(): void {
    this.rowsArray.valueChanges
      .pipe(auditTime(0), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.debugLog('rowsArray.valueChanges -> recomputeAllAndSync()', this.getRowsSnapshot());
        this.recomputeAllAndSync();
      });
  }

  private recomputeAllAndSync(): void {
    this.recomputeAll();
    this.syncRowsToModel();
  }

  private recomputeAll(): void {
    const rows = this.getRowsSnapshot();

    for (let rowIndex = 0; rowIndex < this.rowsArray.length; rowIndex++) {
      const rowGroup = this.rowsArray.at(rowIndex);

      for (const sf of this.cfg.subFields) {
        if (typeof sf.computeValue !== 'function') continue;

        const ctx: RepeatableComputeContext = {
          rowIndex,
          rows: rows as Record<string, string>[],
          model: this.model,
        };

        const computed = sf.computeValue(ctx);
        const control = rowGroup.get(sf.key);
        if (!control) continue;

        if (!this.areEqualLoose(control.value, computed)) {
          if (this.DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[RepeatableGroup][computeValue]', { rowIndex, key: sf.key, from: control.value, to: computed });
          }

          control.patchValue(computed, { emitEvent: false });

          const rowObj = rows[rowIndex];
          if (rowObj) rowObj[sf.key] = computed;
        }
      }
    }
  }

  private syncRowsToModel(): void {
    const { bindings, serialization } = this.cfg;
    const sep = serialization?.separator ?? ';';

    const rows = this.getRowsSnapshot();
    const subFields = this.cfg.subFields ?? [];

    this.debugLog('syncRowsToModel() start', { sep, rows });

    for (const [logicalKey, targetControlName] of Object.entries(bindings ?? {})) {
      const sf = subFields.find((x) => x.key === logicalKey);

      const joined = rows
        .map((r) => this.serializeCellValue(r[logicalKey], sf))
        .join(sep);

      const ctrl = this.model.get(targetControlName);

      if (this.DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[RepeatableGroup][syncRowsToModel]', { logicalKey, targetControlName, joined, prev: ctrl?.value });
      }

      if (ctrl) {
        if (ctrl.value !== joined) ctrl.patchValue(joined, { emitEvent: true });
      } else {
        this.model.addControl(targetControlName, new FormControl(joined));
        this.model.updateValueAndValidity({ emitEvent: true });
      }
    }
  }

  private serializeCellValue(value: unknown, sf?: RepeatableSubField): string {
    if (value == null) return '';

    // In info mode we might store label strings in controls.
    // Keeping that string is OK; it matches what backend provides for info.
    if (!this.isEditable && typeof value === 'string' && value.includes(';')) {
      // Keep semicolon format when syncing back.
      return value;
    }

    const pk = sf?.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;

      if (obj['value'] && typeof obj['value'] === 'object') {
        const inner = obj['value'] as Record<string, unknown>;
        const id = inner[pk] ?? inner['id'];
        return this.valueToString(id);
      }

      const id = obj[pk] ?? obj['id'];
      return this.valueToString(id);
    }

    return this.valueToString(value);
  }

  /**
   * Row count MUST be based on the MAX length among ALL bindings.
   * Also: in info view, dropdown fields may contain LABELS directly, not IDs.
   */
  private hydrateFromModel(): void {
    const { bindings, serialization } = this.cfg;
    const sep = serialization?.separator ?? ';';

    this.debugLog('hydrateFromModel() start', { bindings, sep, isEditable: this.isEditable });

    const entries = Object.entries(bindings ?? {});
    if (entries.length === 0) {
      this.debugWarn('hydrateFromModel(): No bindings found.');
      return;
    }

    // Determine row count from maximum split length across all bound controls
    const splitByControl: Record<string, string[]> = {};
    let maxLen = 0;

    for (const [logicalKey, targetControlName] of entries) {
      const raw = this.model.get(targetControlName)?.value;
      const parts = this.splitSerializedKeepEmpties(raw, sep);

      splitByControl[logicalKey] = parts;
      maxLen = Math.max(maxLen, parts.length);

      this.debugLog('hydrateFromModel(): split', { logicalKey, targetControlName, raw, parts, partsLen: parts.length });
    }

    const desiredRows = Math.min(Math.max(maxLen, 1), this.maxRows);

    this.debugLog('hydrateFromModel(): row sizing', { maxLen, desiredRows, maxRows: this.maxRows });

    while (this.rowsArray.length < desiredRows) this.rowsArray.push(this.createRowGroup());
    while (this.rowsArray.length > desiredRows) this.rowsArray.removeAt(this.rowsArray.length - 1);

    this.pendingDropdown = [];
    this.pendingResolveAttempt = 0;

    for (const [logicalKey] of entries) {
      const sf = this.cfg.subFields.find((f) => f.key === logicalKey);
      if (!sf) {
        this.debugWarn('hydrateFromModel(): subField not found for logicalKey', { logicalKey });
        continue;
      }

      const values = splitByControl[logicalKey] ?? [];

      for (let i = 0; i < this.rowsArray.length; i++) {
        const row = this.rowsArray.at(i);
        const rawValue = values[i] ?? '';
        const rawValueTrim = String(rawValue ?? '').trim();

        if (this.DEBUG) {
          // eslint-disable-next-line no-console
          console.log('[RepeatableGroup][hydrate] cell', { rowIndex: i, logicalKey, rawValue: rawValueTrim, type: sf.input?.type });
        }

        const isSelect =
          sf.input?.type === InputType.DROPDOWN ||
          sf.input?.type === InputType.DROPDOWN_SEARCH ||
          sf.input?.type === InputType.MULTISELECT ||
          sf.input?.type === InputType.MULTISELECT_SEARCH;

        // INFO MODE: If it's a select-like field, backend might send LABELS instead of IDs.
        if (!this.isEditable && isSelect && rawValueTrim) {
          const looksLikeLabel = !this.isNumericLike(rawValueTrim);

          if (looksLikeLabel) {
            // Store label directly (readOnlyCellText will display as-is)
            row.get(logicalKey)?.patchValue(rawValueTrim, { emitEvent: false });

            if (this.DEBUG) {
              // eslint-disable-next-line no-console
              console.log('[RepeatableGroup][hydrate] stored LABEL + skip dropdown mapping', {
                rowIndex: i,
                logicalKey,
                rawValue: rawValueTrim,
              });
            }

            continue;
          }
        }

        // Editable / ID based hydration:
        if (sf.input?.dropdownConfiguration?.valuePrimaryKey) {
          const selectedObj = this.mapIdToDropdownValue(rawValueTrim, sf);

          if (selectedObj) {
            row.get(logicalKey)?.patchValue(selectedObj, { emitEvent: false });
          } else {
            row.get(logicalKey)?.patchValue(null, { emitEvent: false });

            const id = String(rawValueTrim ?? '').trim();
            if (id) this.pendingDropdown.push({ rowIndex: i, logicalKey, rawId: id, sf });
          }
        } else {
          row.get(logicalKey)?.patchValue(rawValueTrim, { emitEvent: false });
        }
      }
    }

    this.debugLog('hydrateFromModel(): pendingDropdown', { pendingCount: this.pendingDropdown.length, pending: this.pendingDropdown });

    if (this.pendingDropdown.length > 0) {
      this.scheduleResolvePending();
    }

    this.recomputeAllAndSync();

    this.debugLog('hydrateFromModel() done', this.getRowsSnapshot());
  }

  private mapIdToDropdownValue(idRaw: unknown, sf: RepeatableSubField): any | null {
    const id = String(idRaw ?? '').trim();
    if (!id) return null;

    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const options = this.dropdownOptions(sf);

    const found = options.find((o) => String(o?.value?.[pk]) === id);

    if (this.DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[RepeatableGroup][mapIdToDropdownValue]', { key: sf.key, id, found: !!found, optionsLen: options.length });
    }

    return found ? found.value : null;
  }

  private scheduleResolvePending(): void {
    if (this.pendingResolveTimer != null) {
      clearTimeout(this.pendingResolveTimer);
      this.pendingResolveTimer = null;
    }

    this.pendingResolveTimer = window.setTimeout(() => {
      this.tryResolvePending();
    }, 50);

    this.debugLog('scheduleResolvePending() setTimeout 50ms');
  }

  private tryResolvePending(): void {
    if (this.pendingDropdown.length === 0) return;

    const MAX_ATTEMPTS = 200; // 200 * 50ms = 10s
    this.pendingResolveAttempt++;

    this.debugLog('tryResolvePending() attempt', { attempt: this.pendingResolveAttempt, pending: this.pendingDropdown.length });

    const stillPending: typeof this.pendingDropdown = [];

    for (const item of this.pendingDropdown) {
      const selectedObj = this.mapIdToDropdownValue(item.rawId, item.sf);

      if (selectedObj) {
        this.rowsArray
          .at(item.rowIndex)
          .get(item.logicalKey)
          ?.patchValue(selectedObj, { emitEvent: false });

        this.debugLog('tryResolvePending() resolved', { item });
      } else {
        stillPending.push(item);
      }
    }

    this.pendingDropdown = stillPending;

    if (this.pendingDropdown.length > 0 && this.pendingResolveAttempt < MAX_ATTEMPTS) {
      this.scheduleResolvePending();
      return;
    }

    if (this.pendingDropdown.length > 0) {
      this.debugWarn('tryResolvePending() gave up unresolved ids', { pending: this.pendingDropdown });
    } else {
      this.debugLog('tryResolvePending() all resolved');
    }
  }

  // -----------------------------
  // Utilities
  // -----------------------------

  private getRowsSnapshot(): RowValue[] {
    const rows: RowValue[] = [];
    for (let i = 0; i < this.rowsArray.length; i++) {
      rows.push({ ...(this.rowsArray.at(i).getRawValue() as RowValue) });
    }
    return rows;
  }

  private splitSerializedKeepEmpties(value: unknown, sep: string): string[] {
    return String(value ?? '')
      .split(sep)
      .map((x) => x.trim());
  }

  private valueToString(value: unknown): string {
    if (value === null || value === undefined) return '';
    return String(value);
  }

  private areEqualLoose(a: unknown, b: unknown): boolean {
    return String(a ?? '') === String(b ?? '');
  }

  private debugLog(message: string, data?: unknown): void {
    if (!this.DEBUG) return;
    // eslint-disable-next-line no-console
    console.log(`[RepeatableGroup] ${message}`, data ?? '');
  }

  private debugWarn(message: string, data?: unknown): void {
    if (!this.DEBUG) return;
    // eslint-disable-next-line no-console
    console.warn(`[RepeatableGroup] ${message}`, data ?? '');
  }
}
